
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/Auth/ProtectedRoute';
import AdminGuard from '@/components/Auth/AdminGuard';
import Layout from '@/components/Layout/Layout';
import ErrorBoundary from '@/components/Layout/ErrorBoundary';
import LanguageGate from '@/components/Auth/LanguageGate';
import { AppConfigProvider } from '@/contexts/AppConfigContext';
import { CrmProvider } from '@/contexts/CrmContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { UserProfileProvider } from '@/contexts/UserProfileContext';
import { ProModalProvider } from '@/contexts/ProModalContext';
import { VoiceCopilotProvider } from '@/contexts/VoiceCopilotContext';
import './App.css';

// ── Eager-loaded (critical path) ───────────────────────────────────────────
import AuthPage from '@/pages/Auth/AuthPage';
import Home from '@/pages/Home';

// ── Lazy-loaded (code-split for performance) ───────────────────────────────
const CustomerList   = lazy(() => import('@/pages/Customers/CustomerList'));
const CustomerDetail = lazy(() => import('@/pages/Customers/CustomerDetail'));
const CustomerForm   = lazy(() => import('@/pages/Customers/CustomerForm'));
const EventList      = lazy(() => import('@/pages/Events/EventList'));
const EventDetail    = lazy(() => import('@/pages/Events/EventDetail'));
const EventForm      = lazy(() => import('@/pages/Events/EventForm'));
const PaymentPage    = lazy(() => import('@/pages/Payments/PaymentPage'));
const PaymentForm    = lazy(() => import('@/pages/Payments/PaymentForm'));
const PublicQuotationForm = lazy(() => import('@/pages/Public/PublicQuotationForm'));
const PublicProposalView = lazy(() => import('@/pages/Public/PublicProposalView'));
const InventoryPage  = lazy(() => import('@/pages/Inventory/InventoryPage'));
const AppSettings    = lazy(() => import('@/pages/Settings/AppSettings'));
const AdminDashboard = lazy(() => import('@/pages/Admin/AdminDashboard'));
const SalesPipeline  = lazy(() => import('@/pages/Sales/SalesPipeline'));
const NotFound       = lazy(() => import('@/pages/NotFound'));

// ── Shared loading fallback ────────────────────────────────────────────────
const PageLoader = () => (
  <div className="flex h-[60vh] w-full items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <UserProfileProvider>
            <AppConfigProvider>
              <NotificationProvider>
                <CrmProvider>
                  <ProModalProvider>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* Public routes - No LanguageGate here to avoid blocking external users */}
                        <Route path="/q/:userId" element={<PublicQuotationForm />} />
                        <Route path="/proposal/:eventId" element={<PublicProposalView />} />

                        {/* Internal app - All behind LanguageGate */}
                        <Route element={<LanguageGate><Outlet /></LanguageGate>}>
                          <Route path="/auth" element={<AuthPage />} />
                          <Route path="/login" element={<Navigate to="/auth" replace />} />

                          {/* Protected App Routes */}
                          <Route element={
                            <ProtectedRoute>
                              <VoiceCopilotProvider>
                                <Layout>
                                  <ErrorBoundary>
                                    <Outlet />
                                  </ErrorBoundary>
                                </Layout>
                              </VoiceCopilotProvider>
                            </ProtectedRoute>
                          }>
                            <Route path="/" element={<Home />} />
                            <Route path="/dashboard" element={<Home />} />
                            <Route path="/sales" element={<SalesPipeline />} />
                            <Route path="/customers" element={<CustomerList />} />
                            <Route path="/customers/new" element={<CustomerForm />} />
                            <Route path="/customers/:id" element={<CustomerDetail />} />
                            <Route path="/customers/:id/edit" element={<CustomerForm />} />
                            <Route path="/events" element={<EventList />} />
                            <Route path="/events/new" element={<EventForm />} />
                            <Route path="/events/:id" element={<EventDetail />} />
                            <Route path="/events/:id/edit" element={<EventForm />} />
                            <Route path="/payments" element={<PaymentPage />} />
                            <Route path="/payments/new" element={<PaymentForm />} />
                            <Route path="/payments/:eventId/new" element={<PaymentForm />} />
                            <Route path="/inventory" element={<InventoryPage />} />
                            <Route path="/settings" element={<AppSettings />} />
                            <Route path="/admin" element={
                              <AdminGuard>
                                <AdminDashboard />
                              </AdminGuard>
                            } />
                          </Route>
                        </Route>

                        {/* 404 */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                  <Toaster />
                  <Sonner />
                  </ProModalProvider>
                </CrmProvider>
              </NotificationProvider>
            </AppConfigProvider>
          </UserProfileProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
